export const KNOWLEDGE_VIEW_EDIT_SESSION_CHANGED = 'KNOWLEDGE_VIEW_EDIT_SESSION_CHANGED';

export type EditResolution = 'save' | 'discard';
export type EditContinuation = () => void | Promise<unknown>;

interface EditSessionPorts {
    getID: () => string | null;
    isDirty: () => boolean;
    setBusy: (busy: boolean) => void;
    persist: (resolution: EditResolution) => Promise<void>;
    finish: () => void;
    prompt: (save: () => void, discard: () => void) => void;
    onError: (error: unknown) => void;
}

/** Panel visibility is deliberately absent: only explicit resolution ends editing. */
export class KnowledgeViewEditSessionGuard {
    private busy = false;
    private request = 0;
    private ports: EditSessionPorts;

    constructor(ports: EditSessionPorts) {
        this.ports = ports;
    }

    async complete(resolution: EditResolution, continuation?: EditContinuation): Promise<void> {
        if (this.busy || !this.ports.getID()) return;
        this.request++;
        this.busy = true;
        this.ports.setBusy(true);
        try {
            if (this.ports.isDirty()) await this.ports.persist(resolution);
            this.ports.finish();
        } catch (error) {
            this.ports.onError(error);
            return;
        } finally {
            this.busy = false;
            this.ports.setBusy(false);
        }
        await continuation?.();
    }

    /** Returns true when the caller must stop; resumes it only after successful resolution. */
    block(continuation?: EditContinuation): boolean {
        if (this.busy) return true;
        const id = this.ports.getID();
        if (!id) return false;
        const request = ++this.request;
        if (!this.ports.isDirty()) {
            this.ports.finish();
            return false;
        }
        const resolve = (resolution: EditResolution) => {
            if (request !== this.request || id !== this.ports.getID()) return;
            void this.complete(resolution, continuation).catch(this.ports.onError);
        };
        // The standard dialog owns visibility. Dismissal invokes neither callback,
        // leaving the session intact and allowing a subsequent navigation attempt.
        this.ports.prompt(() => resolve('save'), () => resolve('discard'));
        return true;
    }
}

/** Edit-mode restrictions apply only while a Knowledge View is open; facility measurements keep their own save behavior. */
export const canSaveMeasurement = (knowledgeViewOpen: boolean, editing: boolean, saved: boolean, transientOnly = false): boolean =>
    !transientOnly && (!knowledgeViewOpen || editing || saved);

export const canPersistMeasurementDeletion = (knowledgeViewOpen: boolean, editing: boolean, saved: boolean): boolean =>
    saved && (!knowledgeViewOpen || editing);
